import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError, API, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CONN } from "../constants/testIds3";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { DownloadSimple, PaperPlaneTilt, Receipt, FloppyDisk } from "@phosphor-icons/react";

async function downloadPath(storagePath) {
  const token = getToken();
  const resp = await fetch(`${API}/files/${storagePath}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) return toast.error("Download failed");
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = storagePath.split("/").pop(); a.click();
  URL.revokeObjectURL(url);
}

const MODES = [
  { v: "export_only", l: "Export only" },
  { v: "portal_ready", l: "Portal-ready (manual upload)" },
  { v: "api_direct", l: "API direct (requires DSC)" },
];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Connectors() {
  const { user, tenantId } = useAuth();
  const [connectors, setConnectors] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [period, setPeriod] = useState(currentMonth());
  const [pfForm, setPfForm] = useState({ mode: "portal_ready", establishment_id: "", employer_code: "", portal_username: "", api_endpoint: "", api_key: "" });
  const [esicForm, setEsicForm] = useState({ mode: "portal_ready", establishment_id: "", employer_code: "", portal_username: "", api_endpoint: "", api_key: "" });

  const load = () => {
    const params = user?.role === "super_admin" && tenantId ? { tenant_id: tenantId } : {};
    api.get("/connectors", { params }).then(({ data }) => {
      setConnectors(data);
      const pf = data.find((c) => c.kind === "pf");
      const esic = data.find((c) => c.kind === "esic");
      if (pf) setPfForm({ ...pfForm, ...pf, api_key: "" });
      if (esic) setEsicForm({ ...esicForm, ...esic, api_key: "" });
    });
    api.get("/connectors/submissions", { params }).then(({ data }) => setSubmissions(data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId, user?.role]);

  const saveConn = async (kind, form) => {
    try {
      const payload = { kind, ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      await api.post("/connectors", payload);
      toast.success(`${kind.toUpperCase()} connector saved`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportFile = async (kind) => {
    try {
      const payload = { period_month: period };
      if (user?.role === "super_admin" && tenantId) payload.tenant_id = tenantId;
      const { data } = await api.post(`/connectors/${kind}/export`, payload);
      toast.success(`${kind.toUpperCase()} file generated · ${data.download_size} bytes`);
      load();
      await downloadPath(data.storage_path);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitToPortal = async (id) => {
    try {
      const { data } = await api.post("/connectors/submit", { submission_id: id });
      toast.success(data.acknowledgement ? `Submitted · ${data.acknowledgement}` : data.message || "Submitted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Compliance</div>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">PF & ESIC connectors</h1>
        <p className="text-sm text-zinc-500 mt-2">Generate ECR (PF) and Monthly Contribution (ESIC) files. Configure portal-ready or (simulated) API-direct mode.</p>
      </div>

      <Tabs defaultValue="pf">
        <TabsList className="bg-zinc-100 border border-zinc-200 rounded-sm">
          <TabsTrigger value="pf" className="rounded-sm">Provident Fund</TabsTrigger>
          <TabsTrigger value="esic" className="rounded-sm">ESIC</TabsTrigger>
          <TabsTrigger value="history" className="rounded-sm">Submissions</TabsTrigger>
        </TabsList>

        <TabsContent value="pf" className="mt-4 space-y-4">
          <ConnectorSection
            kind="pf" title="Provident Fund (EPFO)"
            description="Generates an ECR 2.0 hash-separated file with UAN, wages, and EPF/EPS contributions ready to upload to the EPFO unified portal."
            form={pfForm} setForm={setPfForm}
            onSave={() => saveConn("pf", pfForm)}
            onExport={() => exportFile("pf")}
            period={period} setPeriod={setPeriod}
          />
        </TabsContent>

        <TabsContent value="esic" className="mt-4 space-y-4">
          <ConnectorSection
            kind="esic" title="Employee State Insurance (ESIC)"
            description="Generates the monthly contribution CSV with IP number, days worked and gross wages — accepted for ESIC monthly filing."
            form={esicForm} setForm={setEsicForm}
            onSave={() => saveConn("esic", esicForm)}
            onExport={() => exportFile("esic")}
            period={period} setPeriod={setPeriod}
          />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <div className="overflow-x-auto border border-zinc-200 rounded-sm" data-testid={CONN.submissionsList}>
            <table className="hr-table">
              <thead><tr><th>Kind</th><th>Period</th><th>Format</th><th>Status</th><th>Acknowledgement</th><th>Generated</th><th>Actions</th></tr></thead>
              <tbody>
                {submissions.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500 text-sm">No submissions yet.</td></tr>}
                {submissions.map((s) => (
                  <tr key={s.id}>
                    <td><Badge variant="outline" className="rounded-sm bg-emerald-50 text-emerald-800 border-emerald-200 uppercase">{s.kind}</Badge></td>
                    <td className="font-mono-plex text-xs">{s.period_month}</td>
                    <td className="font-mono-plex text-xs">{s.format}</td>
                    <td><Badge variant="outline" className={`rounded-sm ${{
                        generated: "bg-zinc-100 text-zinc-700 border-zinc-300",
                        portal_ready: "bg-amber-50 text-amber-800 border-amber-200",
                        submitted: "bg-emerald-50 text-emerald-800 border-emerald-200",
                    }[s.status] || ""}`}>{s.status}</Badge></td>
                    <td className="font-mono-plex text-xs">{s.acknowledgement || "—"}</td>
                    <td className="font-mono-plex text-xs">{new Date(s.generated_at).toLocaleString()}</td>
                    <td>
                      <div className="flex gap-1 flex-wrap">
                        <button onClick={() => downloadPath(s.storage_path)} className="text-xs px-2 py-1 border border-zinc-300 hover:bg-zinc-50 rounded-sm"><DownloadSimple size={12} className="inline mr-1" /> File</button>
                        {s.status === "generated" && <button onClick={() => submitToPortal(s.id)} className="text-xs px-2 py-1 border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm" data-testid={`submit-${s.id}`}><PaperPlaneTilt size={12} className="inline mr-1" /> Submit</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConnectorSection({ kind, title, description, form, setForm, onSave, onExport, period, setPeriod }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <section className="border border-zinc-200 bg-white rounded-sm p-5">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-sm bg-emerald-50 grid place-items-center text-emerald-800"><Receipt size={20} weight="duotone" /></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Configuration</div>
            <div className="font-display text-lg font-bold">{title}</div>
          </div>
        </div>
        <p className="text-xs text-zinc-500 mb-4">{description}</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1"><Label className="text-xs">Mode</Label>
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger className="rounded-sm" data-testid={`${kind}-mode-select`}><SelectValue /></SelectTrigger>
              <SelectContent>{MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1"><Label className="text-xs">Establishment ID</Label><Input value={form.establishment_id || ""} onChange={(e) => setForm({ ...form, establishment_id: e.target.value })} className="rounded-sm font-mono-plex" data-testid={`${kind}-est-input`} /></div>
          <div className="space-y-1"><Label className="text-xs">Employer code</Label><Input value={form.employer_code || ""} onChange={(e) => setForm({ ...form, employer_code: e.target.value })} className="rounded-sm font-mono-plex" /></div>
          <div className="col-span-2 space-y-1"><Label className="text-xs">Portal username</Label><Input value={form.portal_username || ""} onChange={(e) => setForm({ ...form, portal_username: e.target.value })} className="rounded-sm" /></div>
          {form.mode === "api_direct" && (
            <>
              <div className="col-span-2 space-y-1"><Label className="text-xs">API endpoint</Label><Input value={form.api_endpoint || ""} onChange={(e) => setForm({ ...form, api_endpoint: e.target.value })} className="rounded-sm font-mono-plex" placeholder="https://…" /></div>
              <div className="col-span-2 space-y-1"><Label className="text-xs">API key</Label><Input type="password" value={form.api_key || ""} onChange={(e) => setForm({ ...form, api_key: e.target.value })} className="rounded-sm font-mono-plex" placeholder="Leave blank to keep existing" /></div>
              <div className="col-span-2 text-[11px] text-orange-700 bg-orange-50 border border-orange-200 rounded-sm px-2 py-1.5">
                API direct mode currently SIMULATES the submission. Production filing requires a Digital Signature Certificate (DSC) integration on your legal entity.
              </div>
            </>
          )}
        </div>
        <div className="mt-4">
          <Button onClick={onSave} className="btn-zenith rounded-sm" data-testid={CONN.saveBtn}>
            <FloppyDisk size={16} className="mr-1.5" /> Save connector
          </Button>
        </div>
      </section>

      <section className="border border-zinc-200 bg-white rounded-sm p-5">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Generate file</div>
        <div className="font-display text-lg font-bold">Return-ready {kind.toUpperCase()} file</div>
        <div className="mt-4 space-y-3">
          <div>
            <Label className="text-xs">Period (must have a payroll run)</Label>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} className="rounded-sm" data-testid={`${kind}-period-input`} />
          </div>
          <Button onClick={onExport} className="btn-zenith rounded-sm" data-testid={kind === "pf" ? CONN.pfExportBtn : CONN.esicExportBtn}>
            <DownloadSimple size={16} className="mr-1.5" /> Generate & download
          </Button>
        </div>
      </section>
    </div>
  );
}
